import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import { getTeam, type TeamMember } from '../../api/teams';
import { useAuthStore } from '../../stores/authStore';
import { useTeamStore } from '../../stores/teamStore';
import { useEventStore } from '../../stores/eventStore';
import ScheduleMap from './components/ScheduleMap';
import MySchedule from './components/MySchedule';
import UnavailabilityManager from './components/UnavailabilityManager';
import ManualAssignmentDialog from './components/ManualAssignmentDialog';

type Tab = 'schedule' | 'mine' | 'unavailabilities';

// eslint-disable-next-line react-refresh/only-export-components
export function canManageTeamSchedule(
  userId: string | null,
  members: Pick<TeamMember, 'user_id' | 'role'>[],
): boolean {
  const role = members.find((member) => member.user_id === userId)?.role;
  return role === 'ADMIN' || role === 'LEADER';
}

export default function SchedulePage() {
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const { activeTeamId, teamMembers, loadTeamMembers } = useTeamStore();
  const {
    events,
    unavailabilities,
    loadEvents,
    loadUnavailabilities,
    addUnavailability,
    removeUnavailability,
  } = useEventStore();

  const [tab, setTab] = useState<Tab>('schedule');
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [ministryId, setMinistryId] = useState('');

  const teamId = activeTeamId ?? '';
  const members = teamId ? (teamMembers[teamId] ?? []) : [];
  const canWriteSetlist = ['ADMIN', 'LEADER', 'EDITOR'].includes(members[0]?.role ?? '');
  const canManageAssignments = canManageTeamSchedule(userId, members);

  useEffect(() => {
    if (!teamId) return;
    loadEvents(teamId);
    loadUnavailabilities(teamId);
    loadTeamMembers(teamId);
  }, [teamId, loadEvents, loadUnavailabilities, loadTeamMembers]);

  useEffect(() => {
    if (!teamId) return;
    getTeam(teamId).then((team) => setMinistryId(team.ministry_id)).catch(() => setMinistryId(''));
  }, [teamId]);

  if (!teamId) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] text-white flex flex-col pt-16">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-white/40">Selecione uma equipe para ver a escala</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white flex flex-col pt-16">
      <Navbar />

      {/* Header */}
      <div className="px-6 md:px-10 py-6 border-b border-white/5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-white">Escala</h1>
            <p className="text-white/30 text-sm mt-1">Atribuições e disponibilidade</p>
          </div>
          {canManageAssignments && tab === 'schedule' && (
            <button
              onClick={() => setShowAssignmentDialog(true)}
              className="px-4 py-2 bg-[#C8A96E] text-[#0A0A0F] font-semibold rounded-xl text-sm hover:bg-[#D4B87A] transition-colors"
            >
              Nova atribuição
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-5 border-b border-white/5">
          {[
            { key: 'schedule', label: 'Escala' },
            { key: 'mine', label: 'Minha Escala' },
            { key: 'unavailabilities', label: 'Indisponibilidades' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key as Tab)}
              className={`px-4 py-2.5 text-sm transition-colors -mb-px ${
                tab === key
                  ? 'text-[#C8A96E] border-b-2 border-[#C8A96E]'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-6 md:px-10 py-6">
        {tab === 'schedule' && (
          <>
            {members.length > 0 && !canManageAssignments && (
              <p className="mb-4 text-sm text-white/40">
                Apenas administradores e líderes da equipe podem criar atribuições na escala.
              </p>
            )}
            <ScheduleMap teamId={teamId} events={events} canWriteSetlist={canWriteSetlist} />
          </>
        )}

        {tab === 'mine' && <MySchedule teamId={teamId} />}

        {tab === 'unavailabilities' && (
          <div className="max-w-2xl">
            <UnavailabilityManager
              items={unavailabilities}
              onAdd={(s, e, r) => addUnavailability(teamId, s, e, r)}
              onDelete={(id) => removeUnavailability(teamId, id)}
            />
          </div>
        )}
      </main>

      {showAssignmentDialog && (
        <ManualAssignmentDialog
          teamId={teamId}
          ministryId={ministryId}
          events={events}
          members={members}
          onClose={() => setShowAssignmentDialog(false)}
          onSaved={() => setShowAssignmentDialog(false)}
        />
      )}
    </div>
  );
}
